import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { BETREIBER, DATENSCHUTZ, ENTWURF, NUTZUNG, STAND, type Abschnitt } from "./texte";

/** Rechtsseiten (W15): Impressum, Datenschutzerklaerung, Nutzungsbedingungen. Ohne Anmeldung
 *  erreichbar; die Angaben des Betreibers kommen vom Server und stehen nicht im Code (P1). */

type Betreiber = { name: string; anschrift: string; email: string; telefon: string };
type Dsb = { name: string; anschrift: string; email: string };
type Rechtliches = { betreiber: Betreiber; vollstaendig: boolean; dsb?: Dsb | null };

async function holeRechtliches(signal?: AbortSignal): Promise<Rechtliches> {
  const antwort = await fetch("/api/rechtliches", { signal, headers: { Accept: "application/json" } });
  if (!antwort.ok) throw new Error(`Server antwortet mit ${antwort.status}`);
  return (await antwort.json()) as Rechtliches;
}

function useBetreiber() {
  return useQuery({ queryKey: ["rechtliches"], queryFn: ({ signal }) => holeRechtliches(signal), staleTime: 60 * 60_000 });
}

/** Der externe Datenschutzbeauftragte (D-062), sobald er in der Server-Konfiguration steht. */
function Datenschutzbeauftragter() {
  const { data } = useBetreiber();
  const d = data?.dsb;
  if (!d?.email) return null;
  return (
    <p className="t-body mt-2 hyphens-auto break-words">
      Unseren Datenschutzbeauftragten erreichst du unter: {[d.name, d.anschrift].filter(Boolean).join(", ")}, E-Mail{" "}
      <a href={`mailto:${d.email}`} className="text-pg-accent-text hover:opacity-70">
        {d.email}
      </a>
      .
    </p>
  );
}

const SEITEN = [
  { pfad: "/impressum", titel: "Impressum" },
  { pfad: "/datenschutz", titel: "Datenschutz" },
  { pfad: "/nutzungsbedingungen", titel: "Nutzungsbedingungen" },
  // § 356a BGB (W17c): die Widerrufsfunktion steht am Ende jeder Seite, hervorgehoben
  { pfad: "/vertrag-widerrufen", titel: "Vertrag widerrufen" },
] as const;

export function Rahmen(props: { titel: string; children: ReactNode }) {
  useEffect(() => {
    document.title = `${props.titel} · Papiergaist`;
  }, [props.titel]);
  return (
    <main className="rand mx-auto max-w-(--w-readable) pb-16">
      <header className="pt-12 pb-2">
        <p className="t-subheadline text-pg-text-2">
          <Link to="/" className="inline-flex min-h-(--touch) items-center text-pg-accent-text hover:opacity-70">
            Papiergaist
          </Link>
        </p>
        <h1 className="recht-titel t-large-title hyphens-auto break-words">{props.titel}</h1>
        <p className="t-footnote mt-1 text-pg-text-2">Stand: {STAND}</p>
      </header>
      {ENTWURF && (
        <p role="note" className="t-subheadline mt-4 rounded-group bg-pg-surface px-4 py-3 text-pg-text-2">
          Entwurf: Dieser Text wird vor dem Start der Beta fertiggestellt.
        </p>
      )}
      {props.children}
      <nav aria-label="Rechtliches" className="mt-10 flex flex-wrap gap-x-6 gap-y-1">
        {SEITEN.map((s) => (
          <Link key={s.pfad} to={s.pfad} className="recht-link t-subheadline inline-flex min-h-(--touch) max-w-full items-center hyphens-auto break-words text-pg-accent-text hover:opacity-70">
            {s.titel}
          </Link>
        ))}
      </nav>
    </main>
  );
}

function Angaben() {
  const { data, error, isPending } = useBetreiber();
  if (isPending) return <p className="t-body text-pg-text-2">Wird geladen …</p>;
  if (error || !data) return <p role="alert" className="t-body text-pg-danger">Die Angaben konnten nicht geladen werden. Bitte später noch einmal versuchen.</p>;
  if (!data.vollstaendig) return <p className="t-body text-pg-text-2">Die Angaben des Betreibers sind noch nicht eingetragen.</p>;
  const b = data.betreiber;
  return (
    <address className="t-body not-italic">
      {b.name}
      {b.anschrift.split(",").map((teil, i) => (
        <span key={`${i}-${teil}`} className="block">
          {teil.trim()}
        </span>
      ))}
      <span className="mt-2 block">
        E-Mail:{" "}
        <a href={`mailto:${b.email}`} className="inline-flex min-h-(--touch) items-center text-pg-accent-text hover:opacity-70">
          {b.email}
        </a>
      </span>
      {b.telefon && <span className="block">Telefon: {b.telefon}</span>}
    </address>
  );
}

/** {BETREIBER} im Text (Widerrufsbelehrung) durch die Angaben vom Server ersetzen; solange sie
 *  fehlen, bleibt ein Hinweis statt einer leeren Stelle. */
function mitBetreiber(text: string, b: Betreiber | undefined): string {
  if (!text.includes(BETREIBER)) return text;
  // das gesetzliche Muster nennt auch die Telefonnummer, soweit vorhanden
  const angaben = b
    ? [b.name, b.anschrift, b.email, b.telefon && `Telefon ${b.telefon}`].filter(Boolean).join(", ")
    : "Name und Anschrift siehe Impressum";
  return text.split(BETREIBER).join(angaben);
}

function Abschnitte(props: { abschnitte: Abschnitt[] }) {
  const { data } = useBetreiber();
  const b = data?.vollstaendig ? data.betreiber : undefined;
  return (
    <>
      {props.abschnitte.map((a) => (
        <section key={a.titel} id={a.id} className="mt-8">
          <h2 className="t-title-3 mb-2">{a.titel}</h2>
          {a.absaetze?.map((text) => (
            <p key={text} className="t-body mt-2 hyphens-auto break-words">
              {mitBetreiber(text, b)}
            </p>
          ))}
          {a.liste && (
            <ul className="t-body mt-2 list-disc space-y-1 pl-5 hyphens-auto">
              {a.liste.map((punkt) => (
                <li key={punkt}>{mitBetreiber(punkt, b)}</li>
              ))}
            </ul>
          )}
          {a.danach?.map((text) => (
            <p key={text} className="t-body mt-2 hyphens-auto break-words">
              {mitBetreiber(text, b)}
            </p>
          ))}
        </section>
      ))}
    </>
  );
}

export function Impressum() {
  return (
    <Rahmen titel="Impressum">
      <section className="mt-8">
        <h2 className="t-title-3 mb-2">Angaben nach § 5 DDG</h2>
        <Angaben />
      </section>
      <section className="mt-8">
        <h2 className="t-title-3 mb-2">Streitbeilegung</h2>
        <p className="t-body hyphens-auto">
          Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
          teilzunehmen.
        </p>
      </section>
    </Rahmen>
  );
}

/** W92: mit Anker (`/datenschutz#freigabe`, Link im Freigabe-Blatt) liegt der Abschnitt nach dem Laden oben im Bild, auch wenn
 *  die Angaben des Betreibers darueber spaeter kommen und die Seite verschieben. Ohne Bewegung (sofort), also auch bei
 *  reduzierter Bewegung still. */
function useAnkerImBild(bereit: boolean) {
  const { hash } = useLocation();
  useLayoutEffect(() => {
    if (hash.length > 1) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.({ block: "start", behavior: "instant" });
  }, [hash, bereit]);
}

export function Datenschutz() {
  const { isPending } = useBetreiber();
  useAnkerImBild(!isPending);
  return (
    <Rahmen titel="Datenschutzerklärung">
      <section className="mt-8">
        <h2 className="t-title-3 mb-2">Verantwortlicher</h2>
        <Angaben />
        <Datenschutzbeauftragter />
      </section>
      <Abschnitte abschnitte={DATENSCHUTZ} />
    </Rahmen>
  );
}

export function Nutzungsbedingungen() {
  return (
    <Rahmen titel="Nutzungsbedingungen">
      <section className="mt-8">
        <h2 className="t-title-3 mb-2">Anbieter</h2>
        <Angaben />
      </section>
      <Abschnitte abschnitte={NUTZUNG} />
    </Rahmen>
  );
}

/** Die drei Links fuer Anmeldung und Einstellungen. */
export function RechtlichesLinks(props: { className?: string }) {
  return (
    <nav aria-label="Rechtliches" className={`flex flex-wrap justify-center gap-x-5 gap-y-1 ${props.className ?? ""}`}>
      {SEITEN.map((s) => (
        <Link key={s.pfad} to={s.pfad} className="recht-link t-footnote inline-flex min-h-(--touch) items-center text-pg-text-2 hover:opacity-70">
          {s.titel}
        </Link>
      ))}
    </nav>
  );
}

/** Einwilligung bei Registrierung und Einladung (W15, W17): zwei getrennte Haken, weil Vertrag
 *  (Nutzungsbedingungen, Alter) und Einwilligung nach Art. 9 DSGVO getrennt gehoeren (Art. 7 Abs. 2).
 *  Ohne beide kein Konto; `wert` ist erst wahr, wenn beide gesetzt sind. Der Server verlangt die
 *  Einwilligung ebenfalls und speichert Zeitpunkt und Fassung. W77 (D-292): mit `onNutzung` ein drittes Haekchen zur
 *  Nutzungsstatistik, vorangewaehlt, ohne Pflicht und ausserhalb von `wert` (Widerspruch, keine Einwilligung); ohne
 *  `onNutzung` fehlt es (neue Fassung der Rechtstexte). */
export function Einwilligung(props: { wert: boolean; onChange: (w: boolean) => void; nutzung?: boolean; onNutzung?: (an: boolean) => void }) {
  const [vertrag, setVertrag] = useState(props.wert);
  const [einwilligung, setEinwilligung] = useState(props.wert);
  const setzen = (v: boolean, e: boolean) => {
    setVertrag(v);
    setEinwilligung(e);
    props.onChange(v && e);
  };
  const haken = (wert: boolean, aendern: (w: boolean) => void, text: ReactNode) => (
    <label className="t-footnote flex min-h-(--touch) items-start gap-1.5 text-pg-text">
      <input type="checkbox" required checked={wert} onChange={(e) => aendern(e.target.checked)} className="mt-1 size-5 shrink-0 accent-(--pg-accent-text)" />
      <span className="hyphens-auto">{text}</span>
    </label>
  );
  return (
    <div className="einwilligung mt-2 flex flex-col gap-1 px-4">
      {haken(vertrag, (w) => setzen(w, einwilligung), "Ich bin mindestens 18 Jahre alt und stimme den Nutzungsbedingungen zu.")}
      {haken(
        einwilligung,
        (w) => setzen(vertrag, w),
        "Ich willige ein, dass Papiergaist meine Post verarbeitet, auch wenn sie besonders schützenswerte Angaben enthält, etwa zur Gesundheit, und dass der Text meiner Briefe zur Einordnung und für den Assistenten an Anthropic in den USA geht. Ich kann die Einwilligung jederzeit widerrufen; das gilt für die Zukunft. Einzelheiten stehen in der Datenschutzerklärung.",
      )}
      {/* Die Links stehen eigens darunter: im Fliesstext waeren sie zu klein zum Treffen; Einzug 26 = Haken 20 + Abstand 6,
          damit sie mit dem Text des Hakens beginnen (R1 Web-5d) */}
      <p className="flex flex-wrap gap-x-2 pl-6.5">
        <RechtLink pfad="/nutzungsbedingungen" text="Nutzungsbedingungen lesen" />
        <RechtLink pfad="/datenschutz" text="Datenschutzerklärung lesen" />
      </p>
      {props.onNutzung && (
        <>
          {/* eigenes Element statt haken(): ohne required, abwaehlen erlaubt die Kontoanlage */}
          <label className="t-footnote flex min-h-(--touch) items-start gap-1.5 text-pg-text">
            <input
              type="checkbox"
              checked={props.nutzung ?? true}
              onChange={(e) => props.onNutzung?.(e.target.checked)}
              className="mt-1 size-5 shrink-0 accent-(--pg-accent-text)"
            />
            <span className="hyphens-auto">{NUTZUNG_HAKEN}</span>
          </label>
          <p className="flex flex-wrap gap-x-2 pl-6.5">
            <RechtLink pfad="/datenschutz" text={NUTZUNG_LINK} />
          </p>
        </>
      )}
    </div>
  );
}

/** W77 (D-292, D-303): Texte des Haekchens und des Hinweis-Blatts (Paket A, wortgleich). Die Datenschutzerklaerung hat
 *  keinen Anker je Abschnitt; der Link fuehrt auf die Seite. */
export const NUTZUNG_HAKEN =
  "Nutzungsstatistik: Wir zählen ohne Inhalte, welche Bildschirme und Funktionen du nutzt und welche Fehler auftreten. In den Einstellungen jederzeit abschaltbar.";
export const NUTZUNG_LINK = "Nutzungsstatistik in der Datenschutzerklärung";

/** Link auf eine Rechtsseite in einem neuen Fenster, gross genug zum Treffen. */
export function RechtLink(props: { pfad: string; text: string }) {
  return (
    <Link
      to={props.pfad}
      target="_blank"
      rel="noopener"
      aria-label={`${props.text} (öffnet ein neues Fenster)`}
      className="t-subheadline block min-h-(--touch) content-center break-words text-pg-accent-text hyphens-auto hover:opacity-70"
    >
      {props.text}
    </Link>
  );
}
